/**
 * @engine/input — Binding
 *
 * Connects a control path to an action.
 * Includes processors and interaction modifiers.
 */

import type {
  BindingDef,
  ControlPath,
  ParsedControlPath,
  ProcessorDef,
  InteractionDef,
  DeviceType,
  ControlValue,
} from '../types';
import type { IInputProcessor } from '../processors/InputProcessor';
import type { IInputInteraction } from '../interactions/InputInteraction';
import { createProcessor } from '../processors';
import { createInteraction } from '../interactions';

/**
 * Parsed control path cache
 */
const pathCache = new Map<ControlPath, ParsedControlPath>();

/**
 * Parse a control path into components.
 * Format: `<DeviceType>/<control>[/<subcontrol>]`
 * Examples:
 * - `<Keyboard>/Space`
 * - `<Gamepad>/leftStick`
 * - `<Gamepad>/leftStick/x`
 * - `<Mouse>/leftButton`
 */
export function parseControlPath(path: ControlPath): ParsedControlPath {
  let cached = pathCache.get(path);
  if (cached) return cached;

  const match = path.match(/^<(\w+)(?:\/(\d+))?>\/(.+)$/);
  if (!match) {
    throw new Error(`Invalid control path: ${path}`);
  }

  const [, device, indexStr, controlPart] = match;
  const parts = controlPart.split('/');

  cached = {
    device: device.toLowerCase() as DeviceType,
    deviceIndex: indexStr ? parseInt(indexStr, 10) : null,
    control: parts[0],
    subControl: parts[1],
  };

  pathCache.set(path, cached);
  return cached;
}


function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Binding — connects a control to an action.
 */
export class Binding {
  /** Original control path */
  readonly path: ControlPath;

  /** Parsed path components */
  readonly parsed: ParsedControlPath;

  /** Control scheme this binding belongs to (or undefined for all) */
  readonly scheme: string | undefined;

  /** Processors applied to this binding's value */
  readonly processors: IInputProcessor[] = [];

  /** Interactions for this binding */
  readonly interactions: IInputInteraction[] = [];

  /** Group for composite bindings */
  readonly group: string | undefined;

  /** Part type for composite bindings */
  readonly compositePart: BindingDef['compositePart'];

  /** Is this binding currently active (scheme matches, etc.) */
  private _active = true;

  constructor(def: BindingDef) {
    this.path = def.path;
    this.parsed = parseControlPath(def.path);
    this.scheme = def.scheme;
    this.group = def.group;
    this.compositePart = def.compositePart;

    // Create processors
    if (def.processors) {
      for (const procDef of def.processors) {
        this.processors.push(createProcessor(procDef));
      }
    }

    // Create interactions
    if (def.interactions) {
      for (const intDef of def.interactions) {
        this.interactions.push(createInteraction(intDef));
      }
    }
  }

  get active(): boolean {
    return this._active;
  }

  set active(value: boolean) {
    this._active = value;
  }

  /**
   * Check if this binding matches the given device
   */
  matchesDevice(deviceType: DeviceType, deviceIndex?: number): boolean {
    if (this.parsed.device !== deviceType) return false;
    if (this.parsed.deviceIndex !== null && deviceIndex !== undefined) {
      return this.parsed.deviceIndex === deviceIndex;
    }
    return true;
  }

  /**
   * Process a raw value through all processors
   */
  processValue(value: ControlValue): ControlValue {
    let result = value;
    for (const proc of this.processors) {
      result = proc.process(result);
    }
    return result;
  }

  /**
   * Serialize to definition
   */
  toDefinition(): BindingDef {
    return {
      path: this.path,
      scheme: this.scheme,
      processors: this.processors.length > 0
        ? this.processors.map((p) => p.toDefinition())
        : undefined,
      interactions: this.interactions.length > 0
        ? this.interactions.map((i) => i.toDefinition())
        : undefined,
      group: this.group,
      compositePart: this.compositePart,
    };
  }

  /**
   * Create from definition
   */
  static fromDefinition(def: BindingDef): Binding {
    return new Binding(def);
  }
}

/**
 * Composite binding — combines multiple bindings into one value.
 * E.g., WASD → Vec2, or two buttons → axis.
 */
export interface CompositeBindingGroup {
  /** Group name */
  name: string;
  /** Bindings in this group */
  bindings: Binding[];
  /** Composite type */
  type: '1daxis' | '2daxis' | '2dvector';
}
